/**
 * Shared contracts between the lanes of discordant.org.
 *
 * Coordinate system (physics + render): metres, Y up, Z toward the viewer, X to the right.
 * The chime ring hangs at CONFIG.ring.center; tubes hang down from anchors on that ring.
 * Screen coordinates are CSS pixels with origin top-left.
 */

export interface Vec2 { x: number; y: number }
export interface Vec3 { x: number; y: number; z: number }

export const v2 = (x = 0, y = 0): Vec2 => ({ x, y });
export const v3 = (x = 0, y = 0, z = 0): Vec3 => ({ x, y, z });

export type QualityTier = 'full' | 'lite';

export interface Quality {
  tier: QualityTier;
  /** device pixel ratio cap for the WebGL layers */
  dprCap: number;
  /** prefers-reduced-motion is set: keep flux gentle, no strobing substitutions */
  reducedMotion: boolean;
  /** primary pointer is coarse (touch) */
  touch: boolean;
}

/** Tube finish. Palette intent: anarchist red/black plus bone and a little gold. */
export type MaterialName = 'iron' | 'oxblood' | 'bone' | 'brass';

export interface TubeSpec {
  index: number;
  /** fundamental frequency (Hz). Audio and physical length both derive from this. */
  freqHz: number;
  /** cord from the ring anchor to the top of the tube (m) */
  cord: number;
  /** tube length (m) */
  length: number;
  /** tube outer radius (m) */
  radius: number;
  /** anchor on the ring, world coordinates */
  anchor: Vec3;
  material: MaterialName;
}

export interface TubeState {
  /**
   * Lean of the hanging line (cord + tube treated as one rigid pendulum) expressed as
   * tangent slopes: lean.x leans toward +X, lean.y leans toward +Z. hangDir() turns this
   * into a unit direction from the anchor toward the bottom of the tube.
   */
  lean: Vec2;
  leanVel: Vec2;
  /**
   * Ring energy 0..1. Raised by strikes, decays over seconds, drained faster when a hand
   * holds the tube. Drives glow, glitch and logo coupling. MUST evolve identically whether
   * audio is muted or not so silent visitors get the full visual experience.
   */
  energy: number;
  /** 0..1 how firmly a hand is currently holding (damping) this tube */
  damped: number;
  /** slow tuning drift in cents, random-walk, updated by physics for the audio mirror */
  detuneCents: number;
}

export interface ClapperState {
  lean: Vec2;
  leanVel: Vec2;
  /** world position of the disc centre (derived each step) */
  pos: Vec3;
  /** disc radius (m) */
  radius: number;
}

export interface SailState {
  lean: Vec2;
  leanVel: Vec2;
  /** world position of the sail centre (derived each step) */
  pos: Vec3;
  /** sail half-size (m); the sail is a flat shape hanging below the clapper */
  halfSize: number;
}

/**
 * A pointer acting as a hand. Positions live on the "hand plane" z = CONFIG.hand.planeZ.
 * Physics treats a hand as reaching through the whole depth of the chimes: proximity is
 * tested in X/Y only, so whatever is under the cursor gets brushed.
 */
export interface HandInput {
  id: number;
  active: boolean;
  pos: Vec3;
  vel: Vec3;
  /** CSS px position of the pointer, for layers that work in screen space */
  screen: Vec2;
  radius: number;
  /** button/finger down */
  pressed: boolean;
  /** seconds this hand has been (nearly) motionless while active */
  still: number;
}

export interface RingSpec {
  center: Vec3;
  /** ellipse semi-axes in X and Z */
  rx: number;
  rz: number;
}

export interface ChimeWorldOptions {
  tuningHz: number[];
  ring: RingSpec;
  seed: number;
  quality: Quality;
}

export type StrikeKind = 'clapper' | 'tube' | 'hand';

export interface StrikeEvent {
  type: 'strike';
  tube: number;
  /** normalised impact strength 0..1 */
  velocity: number;
  kind: StrikeKind;
  /** world position of the impact */
  pos: Vec3;
  /** where along the tube the impact happened, 0 = top, 1 = bottom */
  s: number;
  /** the other tube for tube-tube contacts */
  other?: number;
}

export interface DampEvent {
  type: 'damp';
  tube: number;
  /** 0..1 */
  amount: number;
}

export type PhysicsEvent = StrikeEvent | DampEvent;

export interface ChimeWorld {
  readonly tubes: TubeSpec[];
  readonly tubeStates: TubeState[];
  readonly clapper: ClapperState;
  readonly sail: SailState;
  readonly ring: RingSpec;
  /** hand slots; main.ts writes into these each frame */
  readonly hands: HandInput[];
  /** simulation time in seconds */
  time: number;
  /** current gust strength 0..1 (for atmosphere and the wind bed) */
  readonly gust: number;
  /** advance by a fixed dt (seconds); returns the events produced during that step */
  step(dt: number): PhysicsEvent[];
  /** world position of the point at parameter s (0 top, 1 bottom) along tube i */
  tubePoint(i: number, s: number, out?: Vec3): Vec3;
  /** wind velocity (m/s) at a world position, at the current time */
  windAt(pos: Vec3, out?: Vec3): Vec3;
}

/** Events on the shared bus. */
export interface BusEvents {
  /** a strike, enriched with its CSS-pixel screen position by main.ts */
  strike: StrikeEvent & { screen: Vec2 };
  damp: DampEvent;
  /** several distinct tubes rang within a short window — the collective moment */
  chord: { tubes: number[]; strength: number };
  /** audio state changed */
  audio: AudioState;
  resize: { width: number; height: number; dpr: number };
}

export interface AudioState {
  /** user preference: sound wanted */
  enabled: boolean;
  /** AudioContext is running (a user gesture has happened) */
  unlocked: boolean;
  /** convenience: enabled && unlocked */
  audible: boolean;
}

export interface PointerInfo {
  /** CSS px */
  x: number;
  y: number;
  /** CSS px per second, smoothed */
  vx: number;
  vy: number;
  active: boolean;
  pressed: boolean;
  touch: boolean;
}
